import { useId } from 'react';
import { useTranslation } from 'react-i18next';

const INPUT_CLASS =
  'w-full rounded-lg bg-(--color-bg-tertiary) border border-(--color-border) px-3 py-2 text-(--color-text-primary) text-sm';

export function Field({ label, value, disabled }: Readonly<{ label: string; value: string; disabled?: boolean }>) {
  return (
    <div>
      <label className="block text-sm text-(--color-text-secondary) mb-1">{label}</label>
      <input
        type="text"
        value={value}
        disabled={disabled}
        readOnly={disabled}
        className={`${INPUT_CLASS} disabled:opacity-60`}
      />
    </div>
  );
}

export function NumberField({
  label, value, onChange, min, max,
}: Readonly<{
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  min?: number;
  max?: number;
}>) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block text-sm text-(--color-text-secondary) mb-1">{label}</label>
      <input
        id={id}
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        min={min}
        max={max}
        className={INPUT_CLASS}
      />
    </div>
  );
}

export function SelectField({
  id, label, placeholder, value, options, onChange,
}: Readonly<{
  id: string;
  label: string;
  placeholder: string;
  value: string | null;
  options: readonly { id: string; label: string }[];
  onChange: (v: string | null) => void;
}>) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm text-(--color-text-secondary) mb-1">
        {label}
      </label>
      <select
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value || null)}
        className={INPUT_CLASS}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

export function TextListField({
  id, label, placeholder, value, onChange,
}: Readonly<{
  id: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (text: string) => void;
}>) {
  const { t } = useTranslation();
  return (
    <div>
      <label htmlFor={id} className="block text-sm text-(--color-text-secondary) mb-1">
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={INPUT_CLASS}
      />
      <p className="text-xs text-(--color-text-secondary) mt-1">{t('profile.nutrition.commaSeparated')}</p>
    </div>
  );
}

/** Row of toggle chips; ``selectedClass`` sets the tab's accent colour. */
export function ChipGroup({
  legend, options, isSelected, onToggle, selectedClass,
}: Readonly<{
  legend: string;
  options: readonly { id: string; label: string }[];
  isSelected: (id: string) => boolean;
  onToggle: (id: string) => void;
  selectedClass: string;
}>) {
  return (
    <fieldset>
      <legend className="block text-sm text-(--color-text-secondary) mb-2">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => onToggle(opt.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              isSelected(opt.id)
                ? selectedClass
                : 'bg-(--color-bg-tertiary) text-(--color-text-secondary) border-(--color-border)'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

export function SaveButton({
  onClick, disabled, label, colorClass,
}: Readonly<{ onClick: () => void; disabled: boolean; label: string; colorClass: string }>) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`w-full py-3 rounded-xl font-bold text-base text-gray-900 shadow-lg hover:brightness-110 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed transition-all ${colorClass}`}
    >
      {label}
    </button>
  );
}
